import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Box, Typography, Select, MenuItem, Grow } from '@mui/material';
import Form, { IChangeEvent } from '@rjsf/core';
import validator from '@rjsf/validator-ajv8';
import { ArrayFieldTemplateProps, FieldTemplateProps, TitleFieldProps, WidgetProps } from '@rjsf/utils';
import { colors } from '@src/styles';
import { enhanceSchema } from './schemaEnhancer';
import { RuleListField } from './RuleListField';
import { EnumChipsField } from './EnumChipsField';
import { StrategyCardsWidget } from './StrategyCardsWidget';
import { SwitchCheckboxWidget } from './SwitchCheckboxWidget';
import { CollectionBuilder, ConfigStageBuilder, getCollectionKey, hasConfigFields } from './CollectionBuilder';
import { ReferenceLookupContext, useReferenceLookup } from './references/referenceLookupContext';
import {
  OrganizationReferenceWidget,
  PhaseReferenceWidget,
  WorkflowReferenceWidget,
} from './references/ReferenceLookupWidget';

const monoFontFamily = "'JetBrains Mono', monospace";
const titleFontFamily = "'Inter Tight', 'Inter', sans-serif";

const selectSX = {
  height: 34,
  minWidth: 100,
  borderRadius: '8px',
  backgroundColor: colors.brand.surface,
  color: colors.primary.white,
  fontSize: '12.5px',
  '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.brand.line },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: colors.primary.yellow },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: colors.primary.yellow },
  '& .MuiSelect-icon': { color: colors.brand.textSecondary },
} as const;

const ArrayFieldTemplate = (props: ArrayFieldTemplateProps) => {
  const [selectedIndex, setSelectedIndex] = useState<number>(0);

  useEffect(() => {
    if (selectedIndex >= props.items.length && props.items.length > 0) {
      setSelectedIndex(props.items.length - 1);
    }
  }, [props.items.length, selectedIndex]);

  const currentIndex = selectedIndex >= props.items.length ? Math.max(0, props.items.length - 1) : selectedIndex;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, marginBottom: 1.5, flexWrap: 'wrap' }}>
        {props.items.map((item, index) => {
          const isActive = currentIndex === index;
          return (
            <Box
              key={item.key}
              component="button"
              type="button"
              onClick={() => setSelectedIndex(index)}
              aria-pressed={isActive}
              sx={{
                height: 30,
                paddingX: 1.5,
                borderRadius: '8px',
                cursor: 'pointer',
                fontFamily: titleFontFamily,
                fontSize: '12.5px',
                fontWeight: isActive ? 600 : 500,
                color: isActive ? colors.primary.yellow : colors.brand.textSecondary,
                backgroundColor: isActive ? 'rgba(255, 160, 21, 0.12)' : colors.brand.surface,
                border: `1px solid ${isActive ? 'rgba(255, 160, 21, 0.5)' : colors.brand.line}`,
                transition: 'color 0.15s ease, border-color 0.15s ease, background-color 0.15s ease',
                '&:hover': { borderColor: colors.primary.yellow, color: colors.primary.white },
              }}
            >
              Item {index + 1}
            </Box>
          );
        })}

        {props.canAdd && !props.readonly && !props.disabled && (
          <Box
            component="button"
            type="button"
            className="schema-add-item"
            onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
              props.onAddClick(event);
              setSelectedIndex(props.items.length);
            }}
            sx={{
              height: 30,
              paddingX: 1.5,
              borderRadius: '8px',
              cursor: 'pointer',
              fontFamily: titleFontFamily,
              fontSize: '12.5px',
              fontWeight: 500,
              color: colors.brand.textSecondary,
              backgroundColor: 'transparent',
              border: '1px dashed rgba(255, 255, 255, 0.18)',
              '&:hover': { borderColor: colors.primary.yellow, color: colors.primary.yellow },
            }}
          >
            + Novo item
          </Box>
        )}
      </Box>

      {props.items.length > 0 && props.items[currentIndex] && (
        <Grow in timeout={250} key={currentIndex}>
          <Box sx={{ padding: '4px 0 0' }}>{props.items[currentIndex]}</Box>
        </Grow>
      )}
    </Box>
  );
};

const ARRAY_ITEM_TITLE_PATTERN = /-\d+$/;

const TitleFieldTemplate = ({ id, title, required }: TitleFieldProps) =>
  ARRAY_ITEM_TITLE_PATTERN.test(title) ? null : (
    <legend id={id}>
      {title}
      {required && <span style={{ color: colors.status.rejected, marginLeft: '4px' }}>*</span>}
    </legend>
  );

const FieldTemplate = (props: FieldTemplateProps) => {
  const { id, classNames, label, help, required, description, errors, children, schema, displayLabel, hidden } = props;

  if (hidden) return <div className="hidden">{children}</div>;

  const isBoolean = schema.type === 'boolean';
  const isObject = schema.type === 'object';
  const isArray = schema.type === 'array';

  const needLabel =
    !isBoolean &&
    !isObject &&
    (isArray || displayLabel !== false) &&
    !ARRAY_ITEM_TITLE_PATTERN.test(String(label ?? ''));
  const needDescription = !isObject && !isArray && !isBoolean;

  return (
    <Box className={classNames} sx={{ marginBottom: 1.5, width: '100%' }}>
      {label && needLabel && (
        <Typography
          component="label"
          htmlFor={id}
          sx={{
            display: 'block',
            marginBottom: 0.75,
            fontFamily: titleFontFamily,
            fontSize: '12.5px',
            fontWeight: 500,
            color: colors.brand.textStrong,
          }}
        >
          {label}
          {required && <span style={{ color: colors.status.rejected, marginLeft: '4px' }}>*</span>}
        </Typography>
      )}
      {needDescription && description}
      {children}
      {errors}
      {help}
    </Box>
  );
};

type TValueType = 'string' | 'number' | 'boolean' | 'null';

const getInitialValueType = (value: unknown): TValueType => {
  if (value === null || value === undefined) return 'null';
  if (typeof value === 'boolean') return 'boolean';
  if (typeof value === 'number') return 'number';
  return 'string';
};

const PlainTextWidget = (props: WidgetProps) => (
  <input
    style={{ display: 'flex', flex: 1 }}
    type="text"
    id={props.id}
    value={props.value || ''}
    onChange={(event) => props.onChange(event.target.value || undefined)}
    onBlur={(event) => props.onBlur && props.onBlur(props.id, event.target.value || undefined)}
    onFocus={(event) => props.onFocus && props.onFocus(props.id, event.target.value || undefined)}
    placeholder={props.placeholder}
    disabled={props.disabled}
    readOnly={props.readonly}
    required={props.required}
    autoFocus={props.autofocus}
  />
);

const ValueTypeWidget = (props: WidgetProps) => {
  const [valueType, setValueType] = useState<TValueType>(() => getInitialValueType(props.value));
  const [textValue, setTextValue] = useState<string>(() => {
    if (props.value === null || props.value === undefined) return '';
    return String(props.value);
  });

  const handleTypeChange = (newType: TValueType) => {
    setValueType(newType);

    if (newType === 'null') {
      props.onChange(null);
      setTextValue('');
    } else if (newType === 'boolean') {
      props.onChange(textValue.toLowerCase() === 'true');
    } else if (newType === 'number') {
      const num = Number(textValue);
      props.onChange(isNaN(num) ? 0 : num);
    } else {
      props.onChange(textValue);
    }
  };

  const handleValueChange = (value: string) => {
    setTextValue(value);

    if (valueType === 'null') {
      props.onChange(null);
    } else if (valueType === 'boolean') {
      props.onChange(value.toLowerCase() === 'true');
    } else if (valueType === 'number') {
      const num = Number(value);
      props.onChange(isNaN(num) ? undefined : num);
    } else {
      props.onChange(value || undefined);
    }
  };

  return (
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', width: '100%' }}>
      <Select
        value={valueType}
        onChange={(event) => handleTypeChange(event.target.value as TValueType)}
        inputProps={{ 'aria-label': 'Tipo do valor' }}
        sx={selectSX}
      >
        <MenuItem value="string" sx={{ fontSize: '12.5px' }}>
          Texto
        </MenuItem>
        <MenuItem value="number" sx={{ fontSize: '12.5px' }}>
          Número
        </MenuItem>
        <MenuItem value="boolean" sx={{ fontSize: '12.5px' }}>
          Booleano
        </MenuItem>
      </Select>

      {valueType === 'boolean' ? (
        <Select
          value={textValue.toLowerCase() === 'true' ? 'true' : 'false'}
          onChange={(event) => handleValueChange(event.target.value)}
          size="small"
          disabled={props.disabled || props.readonly}
          inputProps={{ 'aria-label': 'Valor' }}
          sx={{ ...selectSX, flex: 1 }}
        >
          <MenuItem value="true">true</MenuItem>
          <MenuItem value="false">false</MenuItem>
        </Select>
      ) : (
        <input
          id={props.id}
          type={valueType === 'number' ? 'number' : 'text'}
          value={textValue}
          onChange={(event) => handleValueChange(event.target.value)}
          placeholder={props.placeholder}
          disabled={props.disabled}
          readOnly={props.readonly}
          required={props.required}
          style={{ flex: 1 }}
        />
      )}
    </Box>
  );
};

const PHASE_FIELD_PATTERN = /PhaseId$/;

const TypedTextWidget = (props: WidgetProps) => {
  const { idReference, isWorkflowScope } = useReferenceLookup();
  const fieldName = props.name ?? '';
  if (fieldName === 'workflowId' || (fieldName === 'id' && idReference === 'workflow')) {
    return <WorkflowReferenceWidget {...props} />;
  }
  if (fieldName === 'id' && idReference === 'organization') return <OrganizationReferenceWidget {...props} />;
  if (isWorkflowScope && PHASE_FIELD_PATTERN.test(fieldName)) return <PhaseReferenceWidget {...props} />;
  const isValueField = props.label?.toLowerCase() === 'value' || fieldName.toLowerCase() === 'value';
  return isValueField ? <ValueTypeWidget {...props} /> : <PlainTextWidget {...props} />;
};

interface JsonSchemaFormEditorProps {
  schema: object;
  onChange: (formData: string) => void;
  formData: object;
  uiSchema?: object;
  readOnly?: boolean;
  organizationId?: string;
}

const removeEmptyObjects = (obj: unknown): unknown => {
  if (Array.isArray(obj)) {
    return obj.map((item) => {
      const cleaned = removeEmptyObjects(item);
      return cleaned === undefined ? null : cleaned;
    });
  }

  if (obj !== null && typeof obj === 'object') {
    const cleaned: Record<string, unknown> = {};

    for (const [key, value] of Object.entries(obj)) {
      const cleanedValue = removeEmptyObjects(value);

      if (
        cleanedValue !== undefined &&
        cleanedValue !== null &&
        !(typeof cleanedValue === 'object' && !Array.isArray(cleanedValue) && Object.keys(cleanedValue).length === 0)
      ) {
        cleaned[key] = cleanedValue;
      }
    }

    return cleaned;
  }

  return obj;
};

interface StyledSchemaFormProps {
  schema: object;
  formData: unknown;
  onChange: (formData: unknown) => void;
  uiSchema?: object;
  readOnly?: boolean;
}

export const StyledSchemaForm = ({ schema, formData, onChange, uiSchema, readOnly = false }: StyledSchemaFormProps) => {
  const enhanced = useMemo(() => enhanceSchema(schema ?? {}), [schema]);

  return (
    <Box
      sx={{
        backgroundColor: 'transparent',
        fontFamily: titleFontFamily,
        '& .rjsf': {
          '& legend': {
            float: 'none',
            width: 'auto',
            padding: '0 6px',
            marginBottom: '6px',
            fontFamily: monoFontFamily,
            fontSize: '10px',
            fontWeight: 500,
            letterSpacing: '0.16em',
            textTransform: 'uppercase',
            color: colors.brand.textSecondary,
          },
          '& label': {
            color: colors.brand.textStrong,
            fontSize: '12.5px',
            marginBottom: '6px',
            display: 'block',
          },
          '& .form-group': {
            marginBottom: '14px',
            display: 'flex',
            width: '100%',
            gap: 2,
          },
          '& .form-control': {
            marginBottom: '14px',
            display: 'flex',
            gap: 2,
          },
          '& input:not(.MuiSwitch-input), & textarea, & select': {
            boxSizing: 'border-box',
            minHeight: '36px',
            padding: '8px 12px',
            backgroundColor: colors.brand.surface,
            border: `1px solid ${colors.brand.line}`,
            borderRadius: '8px',
            color: colors.primary.white,
            fontFamily: titleFontFamily,
            fontSize: '13.5px',
            transition: 'border-color 0.15s ease',
            '&::placeholder': { color: colors.brand.textMuted },
            '&:hover': { borderColor: 'rgba(255, 160, 21, 0.5)' },
            '&:focus': { outline: 'none', borderColor: colors.primary.yellow },
            '&:disabled': { opacity: 0.5 },
          },
          '& select option': { backgroundColor: colors.brand.surface },
          '& .rjsf-field': {
            display: 'flex',
            flexDirection: 'column',
          },
          '& button:not(.schema-add-item):not(.schema-custom-button)': {
            height: '30px',
            padding: '0 12px',
            backgroundColor: 'rgba(255, 160, 21, 0.1)',
            color: colors.primary.yellow,
            border: '1px solid rgba(255, 160, 21, 0.5)',
            borderRadius: '8px',
            cursor: 'pointer',
            fontFamily: titleFontFamily,
            fontSize: '12.5px',
            fontWeight: 600,
            transition: 'background-color 0.15s ease',
            '&:hover': { backgroundColor: 'rgba(255, 160, 21, 0.18)' },
            '&:disabled': { opacity: 0.5, cursor: 'not-allowed' },
          },
          '& .btn-danger::before': {
            content: "'Remover'",
          },
          '& button.btn-danger': {
            height: '26px',
            padding: '0 10px',
            fontSize: '11.5px',
            fontWeight: 500,
            color: colors.status.rejected,
            backgroundColor: 'transparent',
            border: `1px solid ${colors.status.rejected}55`,
            '&:hover': { backgroundColor: `${colors.status.rejected}14` },
          },
          '& .rjsf-array-item-move-down, & .rjsf-array-item-move-up': {
            display: 'none',
          },
          '& .field-description': {
            marginTop: '4px',
            fontSize: '11.5px',
            lineHeight: 1.5,
            color: colors.brand.textMuted,
            whiteSpace: 'pre-line',
          },
          '& .error-detail, & .errors': {
            margin: '4px 0 0',
            paddingLeft: 0,
            listStyle: 'none',
            color: colors.status.rejected,
            fontSize: '11.5px',
          },
          '& .array-item': {
            backgroundColor: 'transparent',
            marginBottom: '8px',
          },
          '& .array-item-toolbox': {
            flex: '2',
            marginLeft: '40px',
            marginRight: '15px',
          },
          '& fieldset': {
            margin: 0,
            minWidth: 0,
            border: `1px solid ${colors.brand.line}`,
            borderRadius: '10px',
            padding: '12px 14px 4px',
            backgroundColor: 'rgba(255, 255, 255, 0.015)',
          },
          '& .btn-group ': {
            width: '80%',
            justifyContent: 'center',
          },
          '& .row': {
            margin: 0,
            width: '100%',
          },
          '& [class*="col-"]': {
            padding: 0,
            width: '100%',
            maxWidth: '100%',
          },
          '& [class*="panel-"]': {
            padding: 0,
            width: '100%',
            maxWidth: '100%',
          },
          '& [id*="anyof_select"]': {
            display: 'none',
          },
          '& [id*="_description"]': {
            marginBottom: '14px',
            fontSize: '12px',
            color: colors.brand.textMuted,
          },
          '& .checkbox label': {
            display: 'flex',
            flexDirection: 'row-reverse',
            alignItems: 'center',
            justifyContent: 'left',
            gap: '10px',
            fontSize: '13px',
            color: colors.brand.textStrong,
            cursor: 'pointer',
          },
          '& .checkbox input': {
            minHeight: 'auto',
            width: '18px',
            height: '18px',
            padding: 0,
            cursor: 'pointer',
            accentColor: colors.primary.yellow,
          },
          '& .checkbox': {
            marginBottom: '8px',
          },
          ...(readOnly && {
            '& input:disabled, & select:disabled, & input[readonly]': { opacity: 1, cursor: 'default' },
            '& .btn-danger, & .schema-add-item': { display: 'none' },
          }),
        },
      }}
    >
      <Form
        schema={enhanced.schema}
        formData={formData}
        validator={validator}
        onChange={(event: IChangeEvent) => onChange(event.formData)}
        readonly={readOnly}
        liveOmit
        omitExtraData
        uiSchema={{
          ...enhanced.uiSchema,
          'ui:submitButtonOptions': {
            norender: true,
          },
          ...uiSchema,
        }}
        fields={{ RuleListField, EnumChipsField }}
        templates={{
          ArrayFieldTemplate: ArrayFieldTemplate,
          FieldTemplate: FieldTemplate,
          TitleFieldTemplate: TitleFieldTemplate,
        }}
        widgets={{
          TextWidget: TypedTextWidget,
          CheckboxWidget: SwitchCheckboxWidget,
          StrategyCardsWidget,
          WorkflowReferenceWidget,
          OrganizationReferenceWidget,
        }}
      />
    </Box>
  );
};

const JsonSchemaFormEditor: React.FC<JsonSchemaFormEditorProps> = ({
  schema,
  onChange,
  formData,
  uiSchema,
  readOnly = false,
  organizationId,
}) => {
  const collectionKey = useMemo(() => getCollectionKey(schema), [schema]);
  const hasStages = useMemo(() => !collectionKey && hasConfigFields(schema), [collectionKey, schema]);
  const lookupValue = useMemo(() => ({ organizationId }), [organizationId]);

  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const emit = useCallback(
    (data: unknown) => onChangeRef.current(JSON.stringify(removeEmptyObjects(data), null, 2)),
    [],
  );

  const pendingDataRef = useRef<{ data: unknown } | null>(null);
  const emitTimerRef = useRef<number | undefined>(undefined);
  const flushGeneric = useCallback(() => {
    window.clearTimeout(emitTimerRef.current);
    const pending = pendingDataRef.current;
    if (!pending) return;
    pendingDataRef.current = null;
    emit(pending.data);
  }, [emit]);
  const emitGenericSoon = (data: unknown) => {
    pendingDataRef.current = { data };
    window.clearTimeout(emitTimerRef.current);
    emitTimerRef.current = window.setTimeout(flushGeneric, 250);
  };
  useEffect(() => flushGeneric, [flushGeneric]);

  if (collectionKey) {
    return (
      <ReferenceLookupContext.Provider value={lookupValue}>
        <CollectionBuilder
          schema={schema}
          collectionKey={collectionKey}
          formData={formData}
          onChange={emit}
          readOnly={readOnly}
          renderForm={(stageSchema, stageData, onStageChange, stageUiSchema) => (
            <StyledSchemaForm
              schema={stageSchema}
              formData={stageData}
              onChange={onStageChange}
              uiSchema={stageUiSchema}
              readOnly={readOnly}
            />
          )}
        />
      </ReferenceLookupContext.Provider>
    );
  }

  if (hasStages) {
    return (
      <ReferenceLookupContext.Provider value={lookupValue}>
        <ConfigStageBuilder
          schema={schema}
          formData={formData}
          onChange={emit}
          readOnly={readOnly}
          renderForm={(stageSchema, stageData, onStageChange, stageUiSchema) => (
            <StyledSchemaForm
              schema={stageSchema}
              formData={stageData}
              onChange={onStageChange}
              uiSchema={stageUiSchema}
              readOnly={readOnly}
            />
          )}
        />
      </ReferenceLookupContext.Provider>
    );
  }

  return (
    <ReferenceLookupContext.Provider value={lookupValue}>
      <Box onBlurCapture={flushGeneric}>
        <StyledSchemaForm
          schema={schema}
          formData={formData}
          onChange={emitGenericSoon}
          uiSchema={uiSchema}
          readOnly={readOnly}
        />
      </Box>
    </ReferenceLookupContext.Provider>
  );
};

export default JsonSchemaFormEditor;
